import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  RelationshipPriorityKey,
  RelationshipPriorityProfile,
  multiSelectRelationshipPriorityKeys,
  relationshipPrioritiesDataSource,
  relationshipPrioritySections,
  relationshipPriorityValues,
} from "../data/RelationshipPrioritiesDataSource";

const PriorityIcon = ({ icon }: { icon: string }) => {
  const common = {
    className: "h-7 w-7",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };

  if (icon === "target") {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="8" />
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v4M12 18v4" />
      </svg>
    );
  }
  if (icon === "family") {
    return (
      <svg {...common}>
        <circle cx="8" cy="8" r="2.5" />
        <circle cx="16" cy="8" r="2.5" />
        <path d="M3.5 19v-3.5A3.5 3.5 0 0 1 7 12h2a3.5 3.5 0 0 1 3.5 3.5V19M11.5 15.5A3.5 3.5 0 0 1 15 12h2a3.5 3.5 0 0 1 3.5 3.5V19" />
      </svg>
    );
  }
  if (icon === "faith") {
    return (
      <svg {...common}>
        <path d="M4 14.5 9 17l8-4a2 2 0 0 1 2.6.9 2 2 0 0 1-.8 2.6L12 20l-8-3.5Z" />
        <path d="M12 11.5 7.8 7.4a2.8 2.8 0 0 1 4-4L12 4l.2-.6a2.8 2.8 0 0 1 4 4Z" />
      </svg>
    );
  }
  if (icon === "communication") {
    return (
      <svg {...common}>
        <path d="M4 5h16v11H9l-5 4Z" />
        <path d="M8 9h8M8 12h5" />
      </svg>
    );
  }
  if (icon === "location") {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
};

const PinkCheck = () => (
  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white">
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      aria-hidden="true"
    >
      <path d="m5 12 4 4L19 6" />
    </svg>
  </span>
);

const Chevron = () => (
  <svg
    className="h-6 w-6"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    aria-hidden="true"
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

export const RelationshipPrioritiesScreen = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<RelationshipPriorityProfile | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState<RelationshipPriorityKey | null>(
    null,
  );
  const [savingKey, setSavingKey] = useState<RelationshipPriorityKey | null>(
    null,
  );
  const [draftSelections, setDraftSelections] = useState<string[]>([]);
  const [confirmSharingOff, setConfirmSharingOff] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    relationshipPrioritiesDataSource
      .getMe()
      .then(setProfile)
      .catch(() =>
        setError("We could not load your priorities. Please try again."),
      )
      .finally(() => setLoading(false));
  }, []);

  const save = async (
    key: RelationshipPriorityKey,
    value: string | string[] | null,
  ) => {
    setSavingKey(key);
    setError("");
    try {
      const updated = await relationshipPrioritiesDataSource.save({
        [key]: value,
      });
      setProfile(updated);
      setActiveKey(null);
    } catch {
      setError("That choice could not be saved. Please try again.");
    } finally {
      setSavingKey(null);
    }
  };

  const openChoices = (key: RelationshipPriorityKey) => {
    setDraftSelections(relationshipPriorityValues(profile?.answers[key]));
    setActiveKey(key);
  };

  const setSharing = async (enabled: boolean) => {
    setError("");
    try {
      const updated = await relationshipPrioritiesDataSource.save({ enabled });
      setProfile(updated);
      setConfirmSharingOff(false);
    } catch {
      setError("That sharing choice could not be saved. Please try again.");
    }
  };

  const completed = profile?.completed_sections.length ?? 0;
  const total = profile?.total_sections ?? 6;
  const activeSection = activeKey
    ? relationshipPrioritySections[activeKey]
    : null;
  const activeSelection = activeKey ? profile?.answers[activeKey] : undefined;
  const activeSelections = relationshipPriorityValues(activeSelection);
  const isMultiSelect = activeKey
    ? multiSelectRelationshipPriorityKeys.has(activeKey)
    : false;

  return (
    <main className="h-full overflow-y-auto bg-[#fff8ff]">
      <header className="sticky top-0 z-20 flex min-h-[72px] items-center bg-primary px-4 text-white shadow-sm md:px-8">
        <button
          type="button"
          onClick={() => navigate("/profile")}
          className="mr-4 flex h-11 w-11 items-center justify-center rounded-full hover:bg-white/10"
          aria-label="Back to Profile"
        >
          <svg
            className="h-8 w-8"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="m15 18-6-6 6-6" />
            <path d="M9 12h11" />
          </svg>
        </button>
        <h1 className="text-xl font-medium md:text-2xl">
          Relationship Priorities
        </h1>
      </header>

      {loading ? (
        <div className="flex min-h-[55vh] items-center justify-center">
          <span
            className="h-10 w-10 animate-spin rounded-full border-4 border-primary/20 border-t-primary"
            aria-label="Loading priorities"
          />
        </div>
      ) : (
        <div className="mx-auto w-full max-w-2xl px-5 py-8 md:px-8 md:py-10">
          <span className="inline-flex rounded-full border border-primary/25 bg-primary/10 px-3 py-1.5 text-sm font-bold text-primary">
            Optional
          </span>
          <h2 className="mt-6 text-3xl font-bold text-black md:text-4xl">
            Relationship Priorities
          </h2>
          <p className="mt-3 text-base leading-relaxed text-black md:text-lg">
            Complete this anytime to improve your introductions. You can change
            or remove any answer, or opt out at any time. Some questions let
            you choose more than one.
          </p>
          <div className="mt-8">
            <div className="mb-2 text-base font-medium">
              Progress {completed} of {total}
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-primary/10">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${total ? (completed / total) * 100 : 0}%` }}
              />
            </div>
          </div>
          {profile?.enabled === false && (
            <p className="mt-5 rounded-xl border border-primary/20 bg-primary/5 p-4 font-semibold">
              Your relationship priorities are not being shared or used for
              introductions.
            </p>
          )}
          {error && (
            <p className="mt-4 text-sm font-semibold text-red-600">{error}</p>
          )}
          <div className="mt-8 divide-y divide-primary/20">
            {(
              Object.entries(relationshipPrioritySections) as [
                RelationshipPriorityKey,
                (typeof relationshipPrioritySections)[RelationshipPriorityKey],
              ][]
            ).map(([key, section]) => {
              const selected = profile?.answers[key];
              const selectedValues = relationshipPriorityValues(selected);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => openChoices(key)}
                  className="flex w-full items-center gap-4 py-4 text-left md:py-5"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary md:h-14 md:w-14">
                    <PriorityIcon icon={section.icon} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-lg font-bold md:text-xl">
                      {section.title}
                    </span>
                    <span
                      className={`mt-0.5 block text-base ${
                        selected ? "text-primary" : "text-graySecondary"
                      }`}
                    >
                      {selectedValues.length
                        ? selectedValues
                            .map((value) => section.options[value])
                            .filter(Boolean)
                            .join(", ")
                        : "Available to complete"}
                    </span>
                  </span>
                  {selectedValues.length ? <PinkCheck /> : <Chevron />}
                </button>
              );
            })}
          </div>
          <div className="mt-8 flex flex-col gap-2">
            <button
              type="button"
              onClick={() => {
                const next = (
                  Object.keys(
                    relationshipPrioritySections,
                  ) as RelationshipPriorityKey[]
                ).find((key) => !profile?.answers[key]);
                if (next) openChoices(next);
                else navigate("/profile");
              }}
              className="rounded-lg bg-primary px-4 py-3 font-bold text-white"
            >
              {completed === total ? "Done" : "Continue my priorities"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/profile")}
              className="rounded-lg px-4 py-3 font-semibold text-primary hover:bg-primary/5"
            >
              Maybe later
            </button>
            {profile?.enabled === false ? (
              <button
                type="button"
                onClick={() => setSharing(true)}
                className="rounded-lg border border-primary px-4 py-3 font-semibold text-primary hover:bg-primary/5"
              >
                Start sharing my priorities
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmSharingOff(true)}
                className="rounded-lg px-4 py-3 font-semibold text-primary hover:bg-primary/5"
              >
                Stop sharing my priorities
              </button>
            )}
          </div>
        </div>
      )}

      {activeKey && activeSection && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="priority-sheet-title"
        >
          <button
            type="button"
            className="absolute inset-0 cursor-default"
            onClick={() => setActiveKey(null)}
            aria-label="Close choices"
          />
          <div className="relative z-10 max-h-[75vh] w-full max-w-2xl overflow-y-auto rounded-t-[28px] bg-[#fff8ff] px-5 pb-6 pt-3 shadow-2xl">
            <div className="mx-auto mb-5 h-1.5 w-16 rounded-full bg-gray-700" />
            <h3 id="priority-sheet-title" className="text-2xl font-bold">
              {activeSection.title}
            </h3>
            <div className="mt-4">
              {Object.entries(activeSection.options).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  disabled={savingKey !== null}
                  onClick={() => {
                    if (!isMultiSelect) {
                      void save(activeKey, value);
                      return;
                    }
                    setDraftSelections((current) =>
                      current.includes(value)
                        ? current.filter((choice) => choice !== value)
                        : [...current, value],
                    );
                  }}
                  aria-pressed={
                    isMultiSelect
                      ? draftSelections.includes(value)
                      : activeSelections.includes(value)
                  }
                  className="flex w-full items-center gap-5 px-3 py-4 text-left text-lg disabled:opacity-60"
                >
                  <span
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-[3px] ${
                      (isMultiSelect
                        ? draftSelections.includes(value)
                        : activeSelections.includes(value))
                        ? "border-primary"
                        : "border-graySecondary"
                    }`}
                  >
                    {(isMultiSelect
                      ? draftSelections.includes(value)
                      : activeSelections.includes(value)) && (
                      <span className="h-3 w-3 rounded-full bg-primary" />
                    )}
                  </span>
                  <span>{label}</span>
                </button>
              ))}
              {isMultiSelect ? (
                <div className="mt-4 flex gap-3 border-t border-grayLight pt-4">
                  <button
                    type="button"
                    disabled={savingKey !== null}
                    onClick={() => setDraftSelections([])}
                    className="flex-1 rounded-lg border border-primary px-4 py-3 font-semibold text-primary disabled:opacity-60"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    disabled={savingKey !== null}
                    onClick={() =>
                      save(
                        activeKey,
                        draftSelections.length ? draftSelections : null,
                      )
                    }
                    className="flex-1 rounded-lg bg-primary px-4 py-3 font-semibold text-white disabled:opacity-60"
                  >
                    Save choices
                  </button>
                </div>
              ) : (
                <div className="mt-1 border-t border-grayLight pt-1">
                <button
                  type="button"
                  disabled={savingKey !== null}
                  onClick={() => save(activeKey, null)}
                  className="flex w-full items-center gap-5 px-3 py-4 text-left text-lg disabled:opacity-60"
                >
                  <span
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-[3px] ${
                      !activeSelections.length
                        ? "border-primary"
                        : "border-graySecondary"
                    }`}
                  >
                    {!activeSelections.length && (
                      <span className="h-3 w-3 rounded-full bg-primary" />
                    )}
                  </span>
                  <span>Prefer not to answer</span>
                </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {confirmSharingOff && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-5"
          role="dialog"
          aria-modal="true"
          aria-labelledby="stop-sharing-title"
        >
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h3 id="stop-sharing-title" className="text-xl font-bold">
              Stop sharing your priorities?
            </h3>
            <p className="mt-3 leading-relaxed text-graySecondary">
              Your saved answers will be kept, but they will not be shown or
              used for introductions. You can start sharing again at any time.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmSharingOff(false)}
                className="rounded-lg px-4 py-2.5 font-semibold text-primary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setSharing(false)}
                className="rounded-lg bg-primary px-4 py-2.5 font-semibold text-white"
              >
                Stop sharing
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};
